export const services = [
  {
    slug: "ai-discovery-call",
    title: "AI Discovery Call",
    metaDescription:
      "A practical AI discovery call for local businesses that want clear automation opportunities, quick wins, and a realistic next step before investing in implementation.",
    kicker: "Start with clarity",
    heroTitle: "Find the best AI opportunities in your business before you build",
    heroDescription:
      "A focused strategy session to review your workflows, customer touchpoints, and admin bottlenecks so you leave with a short list of useful AI automation ideas and a practical path forward.",
    bestFor: [
      "Owners and operators who know AI could help but are not sure where to start",
      "Local service businesses with repetitive admin, intake, scheduling, or follow-up work",
      "Teams that want a grounded opinion before buying software or starting a build",
    ],
    outcomes: [
      "Identify the highest-value places AI can reduce manual work",
      "Separate realistic automation opportunities from hype",
      "Prioritize quick wins, risks, data needs, and likely effort",
      "Decide whether a done-for-you build, custom engagement, or support plan makes sense",
    ],
    process: [
      {
        title: "Business workflow review",
        description:
          "We walk through how leads, customers, operations, and back-office tasks move through your business today.",
      },
      {
        title: "Automation opportunity map",
        description:
          "We flag repeatable tasks where AI, forms, integrations, or lightweight agents could save time or improve response speed.",
      },
      {
        title: "Next-step recommendation",
        description:
          "You get a practical recommendation for what to automate first, what to avoid, and what information is needed to move forward.",
      },
    ],
    deliverables: [
      "60-minute discovery call",
      "Prioritized AI automation opportunity list",
      "Quick-win and longer-term recommendations",
      "Plain-English implementation notes and risks",
    ],
    faqs: [
      {
        question: "Do I need to understand AI before booking?",
        answer:
          "No. The call is designed for business owners and operators. We focus on business workflows, customer experience, and practical use cases.",
      },
      {
        question: "Will you recommend specific tools?",
        answer:
          "Yes, when it is useful. The goal is not to force a tool choice, but to identify whether off-the-shelf software, simple automation, or custom AI work is the right fit.",
      },
      {
        question: "Is this a sales call?",
        answer:
          "It is a paid strategy session with actionable guidance. If there is a good fit for implementation, we can discuss that as a next step.",
      },
    ],
    cta: "Book an AI discovery call",
  },
  {
    slug: "done-for-you-ai-implementation",
    title: "Done-for-You AI Implementation",
    metaDescription:
      "Done-for-you AI automation implementation for small and local businesses, including workflow design, tool setup, integrations, testing, and launch support.",
    kicker: "From idea to working system",
    heroTitle: "Launch a practical AI automation without adding another project to your plate",
    heroDescription:
      "We design, build, test, and launch AI-powered workflows that reduce manual work in areas like lead intake, customer follow-up, quoting, reporting, and internal operations.",
    bestFor: [
      "Businesses with a clear workflow that is slow, repetitive, or inconsistent",
      "Teams that want implementation handled by an experienced technical partner",
      "Owners who need practical automation connected to existing tools, not a generic chatbot",
    ],
    outcomes: [
      "Replace repetitive manual steps with a reliable AI-assisted workflow",
      "Improve response time for leads, customers, or internal requests",
      "Connect AI output to the tools your team already uses",
      "Launch with documentation, testing, and handoff training",
    ],
    process: [
      {
        title: "Scope and workflow design",
        description:
          "We define the exact workflow, inputs, outputs, tools, approval points, and success criteria before building.",
      },
      {
        title: "Build and integrate",
        description:
          "We configure the automation, prompts, integrations, forms, data sources, and guardrails needed for the workflow to run.",
      },
      {
        title: "Test and refine",
        description:
          "We test with realistic examples, tune outputs, check edge cases, and make sure the system fits how your team actually works.",
      },
      {
        title: "Launch and handoff",
        description:
          "We deploy the workflow, train your team, document how it works, and outline maintenance needs.",
      },
    ],
    deliverables: [
      "Implemented AI-assisted workflow",
      "Prompt and automation configuration",
      "Tool and integration setup",
      "Testing checklist and launch notes",
      "Team handoff documentation",
    ],
    faqs: [
      {
        question: "What kinds of workflows can you implement?",
        answer:
          "Common examples include lead intake, email replies, CRM updates, review responses, customer support triage, estimate drafting, reporting, and internal knowledge search.",
      },
      {
        question: "Do you build custom software every time?",
        answer:
          "No. The right solution may use existing tools, automation platforms, AI APIs, or custom code depending on reliability, budget, and complexity.",
      },
      {
        question: "How do you keep AI from sending bad information?",
        answer:
          "We design workflows with guardrails, human review where needed, clear source material, testing, and escalation paths for uncertain cases.",
      },
    ],
    cta: "Start an AI implementation",
  },
  {
    slug: "custom-ai-engagements",
    title: "Custom AI Engagements",
    metaDescription:
      "Custom AI consulting and development for businesses that need tailored agents, automations, integrations, data workflows, or internal AI tools.",
    kicker: "Built around your operation",
    heroTitle: "Custom AI systems for workflows that off-the-shelf tools cannot handle",
    heroDescription:
      "For businesses with unique processes, messy data, or complex integrations, we design and build AI-enabled tools that fit your operation instead of forcing your team into a generic platform.",
    bestFor: [
      "Businesses with specialized workflows or industry-specific requirements",
      "Teams that need AI connected to private documents, internal systems, or custom APIs",
      "Operators who need technical leadership for an AI project without hiring a full-time team",
    ],
    outcomes: [
      "Turn complex business requirements into a working AI product or workflow",
      "Integrate AI with existing systems, databases, documents, and APIs",
      "Create safer internal tools with permissions, logging, and review steps",
      "Move from prototype to production with a realistic architecture",
    ],
    process: [
      {
        title: "Discovery and technical scoping",
        description:
          "We define users, workflows, data sources, integrations, constraints, risks, and the right level of custom development.",
      },
      {
        title: "Prototype the core workflow",
        description:
          "We build a focused version of the system to prove the approach, test AI quality, and validate user experience.",
      },
      {
        title: "Production build",
        description:
          "We develop the application, automation, agent, or integration with appropriate reliability, security, and maintainability.",
      },
      {
        title: "Rollout and iteration",
        description:
          "We help launch, gather feedback, tune behavior, and plan improvements based on real usage.",
      },
    ],
    deliverables: [
      "Custom AI solution architecture",
      "Prototype or production implementation",
      "Integrations with relevant tools, APIs, or data sources",
      "Prompt, retrieval, and automation logic",
      "Documentation and rollout support",
    ],
    faqs: [
      {
        question: "What makes this different from a standard implementation?",
        answer:
          "Custom engagements are for projects where the workflow, data, integrations, or user experience require a tailored build rather than configuring common tools.",
      },
      {
        question: "Can you work with our existing developer or IT team?",
        answer:
          "Yes. We can provide architecture, implementation, technical leadership, or focused delivery alongside your internal team.",
      },
      {
        question: "Can this include private company data?",
        answer:
          "Yes, when appropriate. We plan data access, permissions, storage, and model usage based on your requirements and risk tolerance.",
      },
    ],
    cta: "Discuss a custom AI project",
  },
  {
    slug: "ai-ops-support-retainer",
    title: "AI Ops Support Retainer",
    metaDescription:
      "Ongoing AI operations support for businesses running AI automations, including monitoring, improvements, prompt tuning, troubleshooting, and advisory support.",
    kicker: "Keep AI useful after launch",
    heroTitle: "Ongoing support for AI automations your business depends on",
    heroDescription:
      "AI workflows need care as tools change, edge cases appear, and your business evolves. The support retainer keeps your automations monitored, improved, and aligned with day-to-day operations.",
    bestFor: [
      "Businesses already using AI automations or preparing to launch them",
      "Teams that want a technical partner available for fixes, tuning, and improvements",
      "Operators who need ongoing AI guidance without a full-time hire",
    ],
    outcomes: [
      "Reduce downtime and confusion when automations break or tools change",
      "Improve AI output quality as real examples come in",
      "Add small enhancements without starting a new project each time",
      "Give your team a trusted point of contact for AI operations questions",
    ],
    process: [
      {
        title: "System review and baseline",
        description:
          "We document current automations, dependencies, known issues, desired service levels, and improvement priorities.",
      },
      {
        title: "Ongoing monitoring and support",
        description:
          "We troubleshoot issues, adjust prompts, review failures, and help keep workflows running smoothly.",
      },
      {
        title: "Monthly improvement planning",
        description:
          "We review performance, identify small upgrades, and prioritize changes that keep AI aligned with business needs.",
      },
    ],
    deliverables: [
      "Recurring support allocation",
      "Prompt and workflow tuning",
      "Troubleshooting and issue resolution",
      "Automation health notes",
      "Monthly recommendations and improvement backlog",
    ],
    faqs: [
      {
        question: "Do you only support systems you built?",
        answer:
          "No. We can review and support existing AI workflows if they are documented enough to operate safely and access can be provided.",
      },
      {
        question: "Is this for emergency support?",
        answer:
          "It is best for ongoing operational support and planned improvements. Critical response expectations can be defined during onboarding if needed.",
      },
      {
        question: "Can unused support time roll into new builds?",
        answer:
          "Retainer structure can be tailored, but the main purpose is to keep existing AI operations healthy and continuously improved.",
      },
    ],
    cta: "Set up AI ops support",
  },
  {
    slug: "small-business-ai-automation",
    title: "Small Business AI Automation",
    metaDescription:
      "AI automation for small businesses that want to save time on leads, admin, customer follow-up, reporting, and day-to-day operations without enterprise complexity.",
    kicker: "Practical automation for busy teams",
    heroTitle: "AI automation that helps small businesses respond faster and work smarter",
    heroDescription:
      "We help local and small businesses automate repeatable work using practical AI workflows that fit existing tools, budgets, and teams.",
    bestFor: [
      "Local service businesses handling calls, forms, emails, estimates, and follow-ups manually",
      "Small teams that need more capacity but are not ready to hire more admin staff",
      "Owners who want simple, useful AI automation without enterprise software complexity",
    ],
    outcomes: [
      "Capture and qualify leads faster",
      "Automate routine replies, reminders, and follow-up tasks",
      "Reduce copy-paste work between email, forms, spreadsheets, and CRM tools",
      "Give staff clearer workflows with AI helping in the background",
    ],
    process: [
      {
        title: "Identify time drains",
        description:
          "We look for repeated tasks in lead handling, scheduling, customer communication, reporting, and admin work.",
      },
      {
        title: "Design simple automations",
        description:
          "We choose practical workflows that can run reliably with your current tools and a manageable level of human review.",
      },
      {
        title: "Implement and train",
        description:
          "We build the automations, test them with real scenarios, and show your team how to use and maintain them.",
      },
      {
        title: "Measure and improve",
        description:
          "We review what changed, where time was saved, and which next automation would create the most value.",
      },
    ],
    deliverables: [
      "Small business automation roadmap",
      "Configured AI workflows and integrations",
      "Lead, customer, or admin automation setup",
      "Testing and quality review",
      "Simple team documentation",
    ],
    faqs: [
      {
        question: "What is a good first automation for a small business?",
        answer:
          "Lead intake and follow-up are often a strong first step because faster responses can improve conversion while reducing repetitive admin work.",
      },
      {
        question: "Will this replace my staff?",
        answer:
          "The goal is usually to remove repetitive busywork so your team can respond faster, stay organized, and spend more time on customers.",
      },
      {
        question: "Can this work with the tools we already use?",
        answer:
          "Often, yes. We commonly work with forms, email, calendars, spreadsheets, CRMs, scheduling tools, and automation platforms before recommending custom software.",
      },
    ],
    cta: "Automate your small business workflow",
  },
  {
    slug: "chatgpt-consultant-small-business",
    title: "ChatGPT Consultant for Small Business",
    metaDescription:
      "ChatGPT consulting for small businesses that want practical prompts, workflows, team guidance, and safe AI usage for everyday operations.",
    kicker: "Make ChatGPT useful at work",
    heroTitle: "ChatGPT consulting for small businesses that need practical workflows",
    heroDescription:
      "We help owners and teams turn ChatGPT from a confusing tool into a repeatable assistant for drafting, summarizing, follow-up, research, and internal operations.",
    bestFor: [
      "Business owners who tried ChatGPT but do not have a consistent workflow",
      "Teams that need reusable prompts, templates, and guardrails",
      "Local businesses that want practical AI help before investing in custom systems",
    ],
    outcomes: [
      "Create reusable prompts for your real business tasks",
      "Train staff on where ChatGPT helps and where human review matters",
      "Build simple workflows for emails, notes, proposals, and research",
      "Reduce risky ad-hoc AI usage by documenting approved patterns",
    ],
    process: [
      {
        title: "Workflow selection",
        description:
          "We identify the tasks where ChatGPT can help right away without creating unnecessary risk or extra complexity.",
      },
      {
        title: "Prompt and template buildout",
        description:
          "We create reusable prompts, checklists, examples, and review steps tailored to your services, tone, and customers.",
      },
      {
        title: "Team handoff",
        description:
          "We document how to use the workflows, what to avoid, and how to review AI outputs before they reach customers.",
      },
    ],
    deliverables: [
      "ChatGPT workflow recommendations",
      "Reusable prompt library",
      "Drafting and review templates",
      "Team usage guidelines",
      "Next-step automation opportunities",
    ],
    faqs: [
      {
        question: "Is this only for ChatGPT?",
        answer:
          "No. ChatGPT is often the easiest starting point, but the same workflow thinking can apply to Claude, Gemini, Microsoft Copilot, or custom AI tools.",
      },
      {
        question: "Can you train my team?",
        answer:
          "Yes. We can show your team how to use approved workflows, evaluate outputs, and avoid putting sensitive data into the wrong tools.",
      },
      {
        question: "What tasks are good candidates?",
        answer:
          "Common starting points include email drafts, meeting summaries, proposal outlines, FAQ responses, social posts, research briefs, and internal SOP drafts.",
      },
    ],
    cta: "Build useful ChatGPT workflows",
  },
  {
    slug: "ai-workflow-automation",
    title: "AI Workflow Automation",
    metaDescription:
      "AI workflow automation for businesses that want to connect forms, email, documents, CRMs, reminders, and AI-assisted decisions into reliable systems.",
    kicker: "Connect the work",
    heroTitle: "AI workflow automation for the handoffs slowing your business down",
    heroDescription:
      "We design and implement AI-assisted workflows that connect your tools, reduce copy-paste work, and make follow-up easier to track.",
    bestFor: [
      "Teams moving information between forms, email, spreadsheets, and CRM tools by hand",
      "Businesses with repeated customer intake, quoting, support, or reporting workflows",
      "Operators who need automation with human review instead of black-box AI decisions",
    ],
    outcomes: [
      "Move information between tools with fewer manual steps",
      "Generate draft replies, summaries, tasks, and reminders from real inputs",
      "Standardize repeatable workflows so work is easier to delegate",
      "Create clear handoffs between AI assistance and human approval",
    ],
    process: [
      {
        title: "Map the current workflow",
        description:
          "We document triggers, inputs, decisions, tools, approvals, and outputs so the automation solves the real bottleneck.",
      },
      {
        title: "Design the AI-assisted path",
        description:
          "We define where AI should summarize, draft, classify, or suggest next steps, and where humans should stay in control.",
      },
      {
        title: "Build and test",
        description:
          "We connect the tools, test with realistic examples, tune prompts, and verify that edge cases are handled safely.",
      },
    ],
    deliverables: [
      "Workflow map and automation design",
      "Configured integrations and AI prompts",
      "Testing examples and edge-case notes",
      "Launch checklist",
      "Documentation for ongoing operation",
    ],
    faqs: [
      {
        question: "What tools can you connect?",
        answer:
          "Common tools include web forms, email, calendars, spreadsheets, CRMs, task managers, document systems, automation platforms, and AI APIs.",
      },
      {
        question: "Can humans approve AI output before it is sent?",
        answer:
          "Yes. For customer-facing or sensitive work, we usually design the workflow so AI drafts or prepares the work and a person reviews it before final action.",
      },
      {
        question: "Do we need custom software?",
        answer:
          "Not always. Many workflows can be built with existing automation platforms and your current tools. Custom code is useful when reliability, complexity, or integration needs justify it.",
      },
    ],
    cta: "Map an AI workflow automation",
  },
];

export default services;
